import React, { useState, useEffect } from 'react';
import { 
  Plus, Search, Users, Edit2, Trash2, X, Save, Mail, 
  Key, Shield, ShieldCheck, ShieldAlert, Package, Send
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  getUsers, createUser, updateUser, deleteUser, 
  resetUserPassword, sendUserCredentials, formatDate, getApiError 
} from '../api';

const QdAccounts = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  
  const currentUser = JSON.parse(localStorage.getItem('qd_user') || '{}');
  
  useEffect(() => {
    loadUsers();
  }, []);
  
  const loadUsers = async () => {
    try {
      const response = await getUsers();
      setUsers(response.data);
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setLoading(false);
    }
  };
  
  const handleDelete = async (user) => {
    if (user.id === currentUser.id) {
      toast.error('Negalite ištrinti savęs');
      return;
    }
    
    if (!confirm(`Ar tikrai norite ištrinti vartotoją "${user.name}"?`)) return;
    
    try {
      await deleteUser(user.id);
      toast.success('Vartotojas ištrintas');
      loadUsers();
    } catch (error) {
      toast.error(getApiError(error));
    }
  };
  
  const handleResetPassword = async (user) => {
    try {
      const response = await resetUserPassword(user.id);
      toast.success(`Naujas slaptažodis: ${response.data.temporary_password}`, {
        duration: 10000
      });
    } catch (error) {
      toast.error(getApiError(error));
    }
  };
  
  const handleSendCredentials = async (user) => {
    try {
      await sendUserCredentials(user.id);
      toast.success('Prisijungimo duomenys išsiųsti');
    } catch (error) {
      toast.error(getApiError(error));
    }
  };
  
  const handleToggleStatus = async (user) => {
    if (user.id === currentUser.id) {
      toast.error('Negalite sustabdyti savo paskyros');
      return;
    }
    
    try {
      const newStatus = user.status === 'active' ? 'suspended' : 'active';
      await updateUser(user.id, { status: newStatus });
      toast.success(newStatus === 'active' ? 'Paskyra aktyvuota' : 'Paskyra sustabdyta');
      loadUsers();
    } catch (error) {
      toast.error(getApiError(error));
    }
  };
  
  const handleToggleEditProducts = async (user) => {
    try {
      await updateUser(user.id, { can_edit_products: !user.can_edit_products });
      toast.success(user.can_edit_products ? 'Katalogo redagavimas išjungtas' : 'Katalogo redagavimas įjungtas');
      loadUsers();
    } catch (error) {
      toast.error(getApiError(error));
    }
  };
  
  const filteredUsers = users.filter(user => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      user.name?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower)
    );
  });
  
  const getRoleIcon = (role) => {
    switch (role) {
      case 'superadmin': return <ShieldCheck size={16} className="text-purple-500" />;
      case 'admin': return <ShieldAlert size={16} className="text-blue-500" />;
      default: return <Shield size={16} className="text-gray-400" />;
    }
  };
  
  const getRoleLabel = (role) => {
    switch (role) {
      case 'superadmin': return 'Super Admin';
      case 'admin': return 'Admin';
      default: return 'Vartotojas';
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Paskyros</h1>
          <p className="text-gray-500">{filteredUsers.length} vartotojų</p>
        </div>
        <button
          onClick={() => { setEditingUser(null); setShowModal(true); }}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#0F7D70] text-white rounded-lg hover:bg-[#0a5f55] transition-colors"
          data-testid="new-user-button"
        >
          <Plus size={20} />
          Naujas vartotojas
        </button>
      </div>
      
      {/* Search */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Ieškoti pagal vardą ar el. paštą..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
          />
        </div>
      </div>
      
      {/* Users List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F7D70]" />
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-100 text-center">
          <Users size={48} className="mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">Nėra vartotojų</h3>
          <p className="text-gray-500">
            {search ? 'Pagal jūsų paiešką nieko nerasta' : 'Pridėkite pirmą vartotoją'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vartotojas</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rolė</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Būsena</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Katalogas</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prisijungta</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Veiksmai</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredUsers.map((user) => (
                  <tr 
                    key={user.id}
                    className={`hover:bg-gray-50 transition-colors ${user.status === 'suspended' ? 'opacity-50' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900">{user.name}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {getRoleIcon(user.role)}
                        <span className="text-sm text-gray-700">{getRoleLabel(user.role)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleToggleStatus(user)}
                        disabled={user.id === currentUser.id}
                        className={`
                          inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium transition-colors
                          ${user.status === 'active' 
                            ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                            : 'bg-red-100 text-red-700 hover:bg-red-200'}
                          ${user.id === currentUser.id ? 'cursor-not-allowed' : ''}
                        `}
                      >
                        {user.status === 'active' ? 'Aktyvus' : 'Sustabdytas'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {user.role === 'user' && (
                        <button
                          onClick={() => handleToggleEditProducts(user)}
                          className={`
                            inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors
                            ${user.can_edit_products 
                              ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
                              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}
                          `}
                        >
                          <Package size={14} />
                          {user.can_edit_products ? 'Taip' : 'Ne'}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-500">
                        {user.last_login ? formatDate(user.last_login) : 'Niekada'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleSendCredentials(user)}
                          className="p-2 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Siųsti prisijungimo duomenis"
                        >
                          <Send size={16} />
                        </button>
                        <button
                          onClick={() => handleResetPassword(user)}
                          className="p-2 text-gray-400 hover:text-orange-500 hover:bg-orange-50 rounded-lg transition-colors"
                          title="Atstatyti slaptažodį"
                        >
                          <Key size={16} />
                        </button>
                        <button
                          onClick={() => { setEditingUser(user); setShowModal(true); }}
                          className="p-2 text-gray-400 hover:text-[#0F7D70] hover:bg-[#0F7D70]/10 rounded-lg transition-colors"
                          title="Redaguoti"
                        >
                          <Edit2 size={16} />
                        </button>
                        {user.id !== currentUser.id && (
                          <button
                            onClick={() => handleDelete(user)}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Ištrinti"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Modal */}
      {showModal && (
        <UserModal
          user={editingUser}
          onClose={() => setShowModal(false)}
          onSaved={() => {
            setShowModal(false);
            loadUsers();
          }}
        />
      )}
    </div>
  );
};

// User Modal
const UserModal = ({ user, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    role: user?.role || 'user'
  });
  const [saving, setSaving] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name.trim()) {
      toast.error('Įveskite vardą');
      return;
    }
    
    if (!form.email.trim()) {
      toast.error('Įveskite el. paštą');
      return;
    }
    
    setSaving(true);
    try {
      if (user) {
        await updateUser(user.id, { name: form.name, role: form.role });
        toast.success('Vartotojas atnaujintas');
      } else {
        await createUser(form);
        toast.success('Vartotojas sukurtas, prisijungimo duomenys išsiųsti el. paštu');
      }
      onSaved();
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Users size={20} className="text-[#0F7D70]" />
            {user ? 'Redaguoti vartotoją' : 'Naujas vartotojas'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Vardas, pavardė *
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              El. paštas *
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                disabled={!!user}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent disabled:bg-gray-100"
                required
              />
            </div>
            {!user && (
              <p className="text-xs text-gray-500 mt-1">
                Prisijungimo duomenys bus išsiųsti automatiškai
              </p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rolė
            </label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
            >
              <option value="user">Vartotojas</option>
              <option value="admin">Admin</option>
              <option value="superadmin">Super Admin</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {form.role === 'user' && 'Gali kurti ir valdyti savo pasiūlymus'}
              {form.role === 'admin' && 'Gali valdyti visus pasiūlymus ir klientus'}
              {form.role === 'superadmin' && 'Pilna prieiga prie visų funkcijų'}
            </p>
          </div>
          
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Atšaukti
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2.5 bg-[#0F7D70] text-white rounded-lg hover:bg-[#0a5f55] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? 'Saugoma...' : (
                <>
                  <Save size={18} />
                  {user ? 'Atnaujinti' : 'Sukurti'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QdAccounts;
